import { useTranslation } from "react-i18next";
import { FormInput } from "@/components/FormInput";
import {
  CountryResource,
  FlowPageInterface,
  PaymentInterface,
  PromotionResource,
  StateResource,
  SubscribePayload,
} from "@/interfaces";
import { useFlow } from "@/contexts/FlowContext.tsx";
import { FormSelect } from "./FormSelect";
import { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "./ui/form";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import useFormRequest from "@/hooks/useFormRequest";
import { AppDialog, AppDialogProps } from "./dialogs/AppDialog";
import { AlertCircle, CircleCheck, Loader } from "lucide-react";
import { axiosInstance } from "@follosoft/react-components";
import { GatewayConfig } from "@/types/payment";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "./ui/skeleton";
import { useOnlinePaymentOptions } from "@/hooks/payments/useOnlinePaymentOptions";
import { planMissesPromotionDeadline, planFirstPaymentDate } from "@/utils/promotion-deadline";
import { paysInFullNow } from "@/utils/pay-in-full";
import {
  blankRequiredPaymentFields,
  composeBillingAddress,
  type RequiredPaymentField,
} from "@/utils/billing-address";
import { formatCurrency, formatDate } from "@/utils/currency";
import { useExtendedUser } from "@/contexts/UserContext";
import { StatusChip } from "./PaymentStatusChip";
import { usePaymentSubscriptions } from "@/hooks/payments/usePaymentSubscriptions";
import { useContract } from "@/hooks/useContract";
import { NuveiFields } from "./payment/NuveiFields";
import { NaxellaFields } from "./payment/NaxellaFields";
import {ContactUs} from "@/components/ContactUs.tsx";
import PaymentGatewayError from "@/components/payment-gateway-error";
import { cardholderName } from "@/utils/client-name";

async function subscribeToPlan(params: {
  contractId: string;
  paymentPlanId: string;
  payload: SubscribePayload;
}) {
  if (!params.paymentPlanId) {
    throw new Error("No payment plan ID");
  }

  if (!params.contractId) {
    throw new Error("No contract ID");
  }

  const { contractId, paymentPlanId, payload } = params;

  return axiosInstance().post(
    `/folloportal/contracts/${contractId}/online-payment/${paymentPlanId}/subscribe`,
    payload
  );
}

/** How the "is required" error names each required payment field. */
const REQUIRED_FIELD_LABELS: Record<RequiredPaymentField, string> = {
  card_holder_name: "validation.fields.cardHolderName",
  billing_address_line1: "validation.fields.billingAddress1",
  billing_city: "validation.fields.billingCity",
  billing_postal_code: "validation.fields.billingPostalCode",
  billing_country: "validation.fields.billingCountry",
  billing_province: "validation.fields.billingProvince",
};

export const FlowPagePayment = (props: FlowPageInterface) => {
  const { t, i18n } = useTranslation();
  const { contractId } = useFlow();
  const { 
    data: contract,
    refetch: refetchContract
  } = useContract(contractId);
  const { user } = useExtendedUser();

  const contractAddressData =
    contract?.relationships?.address?.data?.attributes;
  const company = contract?.relationships?.company?.data;

  const serviceBillingAddress = [
    contractAddressData?.address_no,
    contractAddressData?.address_street,
    contractAddressData?.address_apt,
    contractAddressData?.address_city,
    contractAddressData?.address_province,
    contractAddressData?.address_postalcode,
  ]
    .filter(Boolean)
    .join(", ");

  const hasActiveSubscription = Boolean(
    contract?.relationships?.subscriptionActive?.data?.id
  );

  const [openDialog, setOpenDialog] = useState(false);
  const [status, setStatus] = useState<"processing" | "error" | "success">(
    "processing"
  );
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const { form, loading } = useFormRequest<PaymentInterface, any>(`/contracts/${contractId}/payment`, "POST", {
    meta: {
      card_holder_name: "",
      billing_address_line1: "",
      billing_address_line2: "",
      billing_city: "",
      billing_postal_code: "",
      billing_country: "",
      billing_province: "",
    },
  });

  const { data, isFetching } = useQuery({
    queryKey: ["provinces"],
    queryFn: () => {
      return axiosInstance().get(`countries?include=states`);
    },
    staleTime: 0,
  });

  const countryOptions = useMemo(() => {
    if (!data?.data?.data) return [];

    const selectedCountries = data.data.data.filter(
      (country: CountryResource) => country.attributes.name !== "France"
    );
    return selectedCountries.map((country: CountryResource) => ({
      value: country.id.toString(),
      label: country.attributes.name,
    }));
  }, [data]);

  const selectedCountry = form.watch("meta.billing_country");

  const provinceOptions = useMemo(() => {
    if (!data?.data?.data || !selectedCountry) return [];

    const country = data.data.data.find(
      (c: CountryResource) => c.id.toString() === selectedCountry
    );

    if (!country?.relationships?.states?.data) return [];

    return country.relationships.states.data.map((state: StateResource) => ({
      value: state.attributes.abv,
      label: state.attributes.name,
    }));
  }, [data, selectedCountry]);

  const handleSubmit = form.handleSubmit(async (data) => {
    if (!contract || !props.selectedPaymentPlan?.id) return;

    form.clearErrors([
      "meta.card_holder_name",
      "meta.billing_address_line1",
      "meta.billing_city",
      "meta.billing_postal_code",
      "meta.billing_country",
      "meta.billing_province",
    ]);

    const blankFields = blankRequiredPaymentFields(data.meta, sameAddress);

    blankFields.forEach((field) => {
      form.setError(`meta.${field}`, {
        type: "manual",
        message: t("validation.required", {
          label: t(REQUIRED_FIELD_LABELS[field]),
        }),
      });
    });

    if (blankFields.length > 0) return;

    setOpenDialog(true);
    setStatus("processing");
    setErrorCode(null);

    try {
      if (!gatewayConfig) throw new Error("No gateway config");

      const sessionToken =
        gatewayConfig.provider === "nuvei" ? gatewayConfig.sessionToken : "";

      const addressBillingCustom = composeBillingAddress(data.meta);

      // FIXME Should me more dynamic, in case we add more countries
      const countryCode = data.meta.billing_country === "124" ? "CA" : "US";

      const billingBase = {
        sessionToken,
        cardholderName: data.meta.card_holder_name,
        email: user?.attributes.client_email,
        // Split here rather than by the API: the gateway has nowhere to put a
        // one-field name, and nothing is stored from the split.
        ...cardholderName(user?.attributes),
        // FIXME when we use service address we should use the country (make it by default CA for now)
        country: sameAddress ? "CA" : countryCode,
        address: sameAddress ? serviceBillingAddress : addressBillingCustom,
      };

      if (gatewayConfig.provider === "nuvei") {
        if (!safeCharge || !scFields) throw new Error("Nuvei not ready");

        const tokenResult = await safeCharge.getToken(scFields.cardNumber, {
          sessionToken: gatewayConfig.sessionToken,
          cardHolderName: billingBase.cardholderName,
        });

        // console.log("Nuvei getToken result", tokenResult);
        if (tokenResult.status !== "SUCCESS" || !tokenResult.ccTempToken) {
          const errMsg =
            tokenResult.reason ||
            tokenResult.error?.message ||
            "Tokenization failed";
          
          setStatus("error");
          setErrorCode(errMsg);
          return;
        }

        const ccTempToken = tokenResult.ccTempToken;

        const resp = await subscribeToPlan({
          contractId: contract.id,
          paymentPlanId: props.selectedPaymentPlan?.id,
          payload: {
            ...billingBase,
            ccTempToken,
            paymentMethodTypeId: 500, // Static 500 for credit card
            ...promotionChoicePayload,
            ...payInFullPayload,
          },
        });

        // console.log("Subscription response (nuvei)", resp.data.data);
        const data = resp.data.data;

        if (data.attributes.status === "active") {
          refetchContract();
          refetchOnlinePaymentOptions();
          setStatus("success");
        } else {
          setStatus("error");
          setErrorCode(
            resp.data?.errorCode || resp.data?.reason || "Subscription failed"
          );
          await resetNuvei();
        }
        return;
      }

      if (gatewayConfig.provider === "naxella") {
        if (!props.selectedPaymentPlan?.id)
          throw new Error("Missing payment plan id");
        
        try {
          // Create a unique requestId for this payment request
          const requestId =
            Date.now().toString(36) + Math.random().toString(36).slice(2);
          lastPaymentRequestIdRef.current = requestId;

          const tokenResult = await new Promise<{ token: string; card?: {
            type: string;
            number: string;
            exp: string;
          } }>(
            (resolve, reject) => {
              pendingResolveRef.current = resolve;
              pendingRejectRef.current = reject;

              const evt = new CustomEvent("payment-request", {
                detail: { requestId },
              });
              (window as any).CollectJS.startPaymentRequest(evt);

              setTimeout(() => {
                if (pendingRejectRef.current) {
                  pendingResolveRef.current = null;
                  pendingRejectRef.current = null;
                  reject(new Error("CollectJS tokenization timeout"));
                }
              }, 30000);
            }
          );

          const resp = await subscribeToPlan({
            contractId: contract!.id,
            paymentPlanId: props.selectedPaymentPlan!.id,
            payload: {
              ...billingBase,
              ccTempToken: tokenResult.token,
              paymentMethodTypeId: 500, // Static 500 for credit card
              ...promotionChoicePayload,
              ...payInFullPayload,
            },
          });

          const sub = resp.data?.data;
          if (sub?.attributes?.status === "active") {
            refetchContract();
            refetchOnlinePaymentOptions();
            setStatus("success");
          }
          else {
            setStatus("error");
            setErrorCode(resp.data?.errorCode || resp.data?.reason || "Subscription failed");
          }
        } catch (e: any) {
          const backendApiError = e.response?.data?.message;
          const paymentGatewayError = e.response?.data?.errors?.[0]?.detail;
          setStatus("error");
          setErrorCode(paymentGatewayError || backendApiError || e.message || e.code || "Subscription failed");
        }

        return;
      }
    } catch (e: any) {
      const backendApiError = e.response?.data?.message;
      const paymentGatewayError = e.response?.data?.errors?.[0]?.detail;
      
      setErrorCode(paymentGatewayError || backendApiError || e.code || "Subscription failed");
      setStatus("error");

      if (gatewayConfig?.provider === "nuvei") {
        await resetNuvei();
      }
    }
  });

  type DialogCfg = Omit<AppDialogProps, "open" | "onOpenChange">;
  const cfgByStatus = (companyName: string): Record<
    "processing" | "error" | "success",
    DialogCfg
  > => ({
    processing: {
      icon: <Loader className="h-15 w-15 animate-spin" />,
      title: t("dialogs.loadingPayment.title"),
      description: t("dialogs.loadingPayment.description"),
    },
    error: {
      icon: <AlertCircle className="h-15 w-15 text-destructive" />,
      title: t("dialogs.paymentError.title"),
      description: t("dialogs.paymentError.description", {
        errorCode: errorCode ?? "400",
      })
    },
    success: {
      icon: <CircleCheck className="h-15 w-15 text-green-500" />,
      title: t("dialogs.paymentSuccess.title"),
      description: t("dialogs.paymentSuccess.description", { companyName }),
      actions: [
        {
          label: t("ui.continue"),
          onClick: () => {
            setOpenDialog(false);
            props.nextStep();
          },
        },
      ],
    },
  });

  const cfg = cfgByStatus(contract?.relationships?.company?.data?.attributes.name || "");

  const [gatewayConfig, setGatewayConfig] = useState<GatewayConfig | null>(
    null
  );
  const [paymentGatewayLoading, setPaymentGatewayLoading] = useState(false);
  const [paymentGatewayError, setPaymentGatewayError] = useState<string | null>(
    null
  );
  const [gatewayRetryKey, setGatewayRetryKey] = useState(0);

  const [safeCharge, setSafeCharge] = useState<any>(null);
  const [scFields, setScFields] = useState<{
    cardNumber: any;
    cardExpiry: any;
    cardCvc: any;
  } | null>(null);
  const [fieldsReady, setFieldsReady] = useState<boolean>(false);
  const [sameAddress, setSameAddress] = useState<boolean>(false);

  // State for Naxella Payment
  const lastPaymentRequestIdRef = useRef<string | null>(null);
  const pendingResolveRef = useRef<
    null | ((v: { token: string; card?: any }) => void)
  >(null);
  const pendingRejectRef = useRef<null | ((e: any) => void)>(null);

  const eligiblePromotion = props.selectedPaymentPlan?.relationships
    ?.eligiblePromotions?.data?.[0] as PromotionResource | undefined;

  // Chosen on the payment agreement step, and only ever true for a plan that
  // offers it: the API refuses it for any other, so the quote must not price on it.
  const paysInFull = paysInFullNow(props.selectedPaymentPlan, props.payInFull);

  // Settling today pays before every deadline, so a plan being paid in full has no
  // deadline left to miss.
  const promotionEnforcesDeadline =
    !paysInFull &&
    planMissesPromotionDeadline(
      eligiblePromotion,
      planFirstPaymentDate(props.selectedPaymentPlan),
    );

  // Keeping the plan's own schedule gives up the discount only when that schedule
  // would pay after the deadline, so nothing here may keep advertising it.
  const forfeitsDeadlineDiscount =
    promotionEnforcesDeadline && props.paymentTiming === "plan_schedule";

  const selectedPromotionId =
    eligiblePromotion?.id
    ?? contract?.relationships?.subscriptionActive?.data?.attributes?.payment_snapshot?.appliedPromotion?.id;

  // Chosen on the payment agreement step. Only a deadline-bearing promotion gets a
  // say in the payment date; every other plan keeps its own schedule, so no timing
  // is sent and nothing changes.
  const effectivePaymentTiming = promotionEnforcesDeadline ? props.paymentTiming : undefined;

  // Sent on submit so the backend applies the promotion the customer was quoted
  // rather than re-picking one, and honours a declined discount.
  const promotionChoicePayload = selectedPromotionId
    ? {
        promotionId: Number(selectedPromotionId),
        ...(effectivePaymentTiming ? { paymentTiming: effectivePaymentTiming } : {}),
      }
    : {};

  // The whole balance is charged today instead of the plan's first instalment.
  const payInFullPayload = paysInFull ? { payInFull: true } : {};

  const { data: paymentSubscriptionData, refetch: refetchOnlinePaymentOptions } = useOnlinePaymentOptions(
    contractId,
    props.selectedPaymentPlan?.id,
    selectedPromotionId,
    effectivePaymentTiming,
    paysInFull
  );

  const {
    data: listOfPaymentSubscriptions
  } = usePaymentSubscriptions(
    contractId,
    contract?.relationships?.subscriptionActive?.data?.id
  );

  const subscriptionsData = hasActiveSubscription
  ? listOfPaymentSubscriptions?.relationships?.schedules?.data
  : paymentSubscriptionData?.data?.meta?.schedule;
  

  useEffect(() => {
    if (!contract) return;
    if (
      !props.currentlyVisible ||
      !props.selectedPaymentPlan?.id ||
      hasActiveSubscription
    )
      return;

    let cancelled = false;

    (async () => {
      try {
        setPaymentGatewayLoading(true);
        setPaymentGatewayError(null);
        setGatewayConfig(null);
        setFieldsReady(false);
        setSafeCharge(null);
        setScFields(null);

        const meta = paymentSubscriptionData?.meta;
        const gateway = meta?.gateway_config;

        if (cancelled) return;

        if (gateway?.provider === "nuvei") {
          const merchantId = gateway.merchant_id;
          const merchantSiteId = gateway.merchant_site_id;
          const environment = gateway.environment;

          if (!merchantId || !merchantSiteId) {
            throw new Error("Missing Nuvei merchant credentials");
          }

          const tokenRes = await axiosInstance().post(
            `/folloportal/contracts/${contract.id}/online-payment/session-token`
          );

          const sessionToken =
            tokenRes.data?.data?.attributes?.session_token ??
            tokenRes.data?.sessionToken;

          if (!sessionToken) throw new Error("Missing Nuvei session token");

          setGatewayConfig({
            provider: "nuvei",
            sessionToken,
            merchantId: merchantId as string,
            merchantSiteId: merchantSiteId as string,
            environment: environment,
          });
        } else if (gateway?.provider === "naxella") {
          // We need the same similar to blade use case:
          // data-tokenization-key="{{ $this->publicTokenizationKey() }}"
          const publicTokenizationKey = gateway.tokenization_key;
          if (!publicTokenizationKey) {
            throw new Error("Missing Naxella public tokenization key");
          }

          setGatewayConfig({
            provider: "naxella",
            publicTokenizationKey: String(publicTokenizationKey),
          });
        } else {
          throw new Error(`Unsupported payment provider`);
        }
      } catch (err: any) {
        console.error(err);
        if (!cancelled) {
          const status = err?.response?.status;
          const detail =
            err?.response?.data?.errors?.[0]?.detail ||
            err?.response?.data?.message ||
            err?.message;
          setPaymentGatewayError(
            detail || (status ? `HTTP ${status}` : "Unknown error")
          );
        }
      } finally {
        if (!cancelled) setPaymentGatewayLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    contract, 
    props.currentlyVisible, 
    props.selectedPaymentPlan, 
    paymentSubscriptionData?.meta,
    hasActiveSubscription,
    gatewayRetryKey,
  ]);

  const [nuveiMountKey, setNuveiMountKey] = useState(0);

  const resetNuvei = async () => {
    if (!contract?.id) return;

    try {
      setPaymentGatewayLoading(true);
      setPaymentGatewayError(null);

      // wipe current instances so we can’t accidentally reuse them
      setFieldsReady(false);
      setSafeCharge(null);
      setScFields(null);

      // get a fresh session token
      const tokenRes = await axiosInstance().post(
        `/folloportal/contracts/${contract.id}/online-payment/session-token`
      );

      const sessionToken =
        tokenRes.data?.data?.attributes?.session_token ?? tokenRes.data?.sessionToken;

      if (!sessionToken) throw new Error("Missing Nuvei session token");

      setGatewayConfig((prev) =>
        prev && prev.provider === "nuvei"
          ? { ...prev, sessionToken }
          : prev
      );

      // FORCE re-mount of NuveiFields
      setNuveiMountKey((k) => k + 1);
    } catch (e: any) {
      console.error(e);
      const status = e?.response?.status;
      const detail =
        e?.response?.data?.errors?.[0]?.detail ||
        e?.response?.data?.message ||
        e?.message;
      setPaymentGatewayError(
        detail || (status ? `HTTP ${status}` : "Impossible de préparer le paiement. Réessayez plus tard.")
      );
    } finally {
      setPaymentGatewayLoading(false);
    }
  };

  const promoPrices = (forfeitsDeadlineDiscount ? undefined : eligiblePromotion) as PromotionResource;

  const displayTotal = promoPrices?.attributes?.new_total
    ?? contract?.meta?.prices?.total ?? 0;

  // The snapshot fallbacks describe a promotion already locked into a
  // subscription; neither may resurrect a discount the customer just declined.
  const promoName = forfeitsDeadlineDiscount
    ? undefined
    : promoPrices?.attributes?.name ??
      contract?.meta?.payment_snapshot?.appliedPromotion?.name ??
      contract?.relationships?.subscriptionActive?.data?.attributes
        ?.payment_snapshot?.appliedPromotion?.name;

  if (!props.currentlyVisible || !props.selectedPaymentPlan) {
    return <></>;
  }

  const canSubmit =
    !loading &&
    !paymentGatewayLoading &&
    !paymentGatewayError &&
    fieldsReady &&
    ((gatewayConfig?.provider === "nuvei" && !!safeCharge && !!scFields) ||
      gatewayConfig?.provider === "naxella");

  return (
    <>
      <Form {...form}>
        <div className="grid lg:grid-cols-3 grid-cols-1 gap-9">
          <div className="col-span-1 md:col-span-2 relative">
            <div className="follo-card">
              <section className="p-7 pb-0">
                <h3 className="follo-label mb-5 relative">
                  {t("fe.pages.flow.steps.payment.title")}
                </h3>
                {paymentGatewayError && (
                  <PaymentGatewayError
                    message={paymentGatewayError}
                    onRetry={() => setGatewayRetryKey((k) => k + 1)}
                    retrying={paymentGatewayLoading}
                  />
                )}
                {/* CARD HOLDER NAME */}
                <FormInput
                  isRequired
                  className={`mb-5 gap-1 ${hasActiveSubscription ? "hidden" : ""}`}
                  form={form}
                  label={t("models.paymentInfo.cardHolderName")}
                  name="meta.card_holder_name"
                  placeholder=""
                  disabled={hasActiveSubscription}
                />
                {!paymentGatewayError && (paymentGatewayLoading || (!gatewayConfig && !hasActiveSubscription)) && (
                  <CardFieldsSkeleton />
                )}
                
                {/* NUVEI FIELDS */}
                {gatewayConfig?.provider === "nuvei" &&
                  !paymentGatewayLoading &&
                  gatewayConfig.merchantId &&
                  gatewayConfig.merchantSiteId && (
                    <NuveiFields
                      key={`${gatewayConfig.sessionToken}-${nuveiMountKey}`}
                      visible={props.currentlyVisible && !hasActiveSubscription}
                      disabled={hasActiveSubscription}
                      merchantId={gatewayConfig.merchantId}
                      merchantSiteId={gatewayConfig.merchantSiteId}
                      environment={gatewayConfig.environment}
                      onReadyChange={setFieldsReady}
                      onInit={({ safeCharge, scFields }) => {
                        setSafeCharge(safeCharge);
                        setScFields(scFields);
                      }}
                    />
                )}

                {/* NAXELLA FIELDS */}
                {gatewayConfig?.provider === "naxella" &&
                  !paymentGatewayLoading &&
                  gatewayConfig.publicTokenizationKey && (
                    <NaxellaFields
                      visible={props.currentlyVisible && !hasActiveSubscription}
                      disabled={hasActiveSubscription}
                      publicTokenizationKey={gatewayConfig.publicTokenizationKey}
                      onReadyChange={setFieldsReady}
                      lastPaymentRequestIdRef={lastPaymentRequestIdRef}
                      pendingResolveRef={pendingResolveRef}
                      pendingRejectRef={pendingRejectRef}
                    />
                  )}
                <h3 className="follo-label mb-5 mt-6">
                  {t("fe.pages.flow.steps.payment.billingAddress")}
                </h3>
                {!hasActiveSubscription && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={sameAddress}
                      onCheckedChange={() => {
                        form.clearErrors();
                        setSameAddress(!sameAddress)
                      }}
                      id="sameAddress"
                      name="sameAddress"
                      disabled={hasActiveSubscription}
                    />
                    <Label htmlFor="sameAddress">
                      {t("models.paymentInfo.sameAddress")}
                      <span className="text-primary font-medium">
                        "{serviceBillingAddress}"
                      </span>
                    </Label>
                  </div>
                )}
                <FormInput
                  className="my-6 gap-1"
                  form={form}
                  label={t("models.paymentInfo.billingAddress1")}
                  name="meta.billing_address_line1"
                  placeholder=""
                  disabled={sameAddress || hasActiveSubscription}
                  isRequired={!sameAddress}
                />
                <FormInput
                  className="mb-6 gap-1"
                  form={form}
                  label={t("models.paymentInfo.billingAddress2")}
                  name="meta.billing_address_line2"
                  placeholder=""
                  disabled={sameAddress || hasActiveSubscription}
                />
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <FormInput
                    className="mb-6 gap-1"
                    form={form}
                    label={t("models.paymentInfo.billingCity")}
                    name="meta.billing_city"
                    placeholder=""
                    disabled={sameAddress || hasActiveSubscription}
                    isRequired={!sameAddress}
                  />
                  <FormInput
                    className="mb-6 gap-1"
                    form={form}
                    label={t("models.paymentInfo.billingPostalCode")}
                    name="meta.billing_postal_code"
                    placeholder=""
                    disabled={sameAddress || hasActiveSubscription}
                    isRequired={!sameAddress}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <FormSelect
                    className="mb-6 gap-1"
                    form={form}
                    label={t("models.paymentInfo.billingCountry")}
                    name="meta.billing_country"
                    placeholder={t(
                      "models.paymentInfo.billingCountryPlaceholder"
                    )}
                    options={countryOptions}
                    disabled={
                      isFetching || sameAddress || hasActiveSubscription
                    }
                    isRequired={!sameAddress}
                  />
                  <FormSelect
                    className="mb-6 gap-1"
                    form={form}
                    label={t("models.paymentInfo.billingProvince")}
                    name="meta.billing_province"
                    placeholder={t(
                      "models.paymentInfo.billingProvincePlaceholder"
                    )}
                    options={provinceOptions}
                    disabled={
                      !Boolean(selectedCountry) || isFetching || sameAddress
                    }
                    isRequired={!sameAddress}
                  />
                </div>

                <p className="text-sm text-muted-foreground mb-6">
                  {t("models.paymentInfo.paymentInfoCompanyAuthorization", {
                    companyName: "Company Name",
                  })}
                </p>
              </section>
              <hr className="border-border" />
              <div className="text-right py-4 px-6">
                {!hasActiveSubscription ? (
                  <Button
                    color="primary"
                    type="submit"
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                    className="cursor-pointer"
                  >
                    {loading ? t("ui.loading") : t("ui.confirm")}
                  </Button>
                ) : (
                  <Button
                    className="cursor-pointer"
                    color="primary"
                    type="submit"
                    onClick={() => props.nextStep()}
                  >
                    {t("ui.next")}
                  </Button>
                )}
              </div>
            </div>
            <ContactUs email={company?.attributes.email} />
          </div>
          <div className="flex flex-col gap-8 col-span-1">
            <section className="follo-card px-8 py-7">
              <h3 className="follo-label mb-5">
                {t("fe.pages.flow.steps.payment.title2")}
              </h3>

              <div className="flex justify-between text-sm leading-5 font-normal text-card-foreground mb-4 gap-4">
                <div>{t("fe.pages.flow.steps.payment.totalContract")}</div>
                <div>{formatCurrency(displayTotal, i18n.language)}</div>
              </div>

              {promoName && (
                <div className="flex justify-between text-sm leading-5 font-normal text-[#0A4DBD] mb-4 gap-4">
                  <div>
                    {t("fe.pages.flow.steps.summary.details.promotionApplied", {
                      defaultValue: "Promotion applied",
                    })}
                  </div>
                  <div className="text-right font-medium">{promoName}</div>
                </div>
              )}

              <div className="flex justify-between text-sm leading-5 font-semibold text-destructive mb-6">
                <div>{t("fe.pages.flow.steps.payment.balanceLeft")}</div>
                <div>{formatCurrency(paymentSubscriptionData?.meta?.balance, i18n.language)}</div>
              </div>

              <hr className="border-border mb-5" />

              <div className="flex justify-between text-base leading-6 font-semibold text-primary">
                <div>{t("fe.pages.flow.steps.payment.paymentAmount")}</div>
                <div>
                  {formatCurrency(
                    paymentSubscriptionData?.data?.meta?.initial_payment?.amount,
                    i18n.language
                  )}
                </div>
              </div>
            </section>

            <section className="follo-card px-8 py-7">
              <h3 className="follo-label mb-5">
                {t("fe.pages.flow.steps.payment.title3")}
              </h3>

              {!hasActiveSubscription && paymentSubscriptionData?.data?.meta?.initial_payment && (
                <div
                  className={`flex justify-between text-sm leading-5 font-normal text-card-foreground gap-4 ${
                    paymentSubscriptionData?.data?.meta?.schedule?.length
                      ? "mb-4"
                      : ""
                  }`}
                >
                  <div className="flex flex-col gap-1">
                    <span className="mb-1">
                      {formatDate(
                        paymentSubscriptionData?.data?.meta?.initial_payment?.date,
                        i18n.language
                      )}
                    </span>
                    <StatusChip
                      label={t("fe.pages.flow.steps.payment.statusWaiting")}
                      variant="pending"
                    />
                  </div>
                  <div>
                    {formatCurrency(
                      paymentSubscriptionData?.data?.meta?.initial_payment?.amount,
                      i18n.language
                    )}
                  </div>
                </div>)
              }

              {subscriptionsData?.map(
                (scheduledPayment: any, index: number) => (
                  <div key={index} className="flex justify-between text-sm leading-5 font-normal text-card-foreground mb-4 last:mb-0 gap-4">
                    <div className="flex flex-col gap-1">
                      <span className="mb-1">
                        {formatDate(scheduledPayment.date || scheduledPayment?.attributes?.date, i18n.language)}
                      </span>
                      {scheduledPayment?.attributes?.is_paid ? (
                        <StatusChip
                          label={t("fe.pages.flow.steps.payment.statusPayed")}
                          variant="paid"
                        />
                      ) : (
                        <StatusChip
                          label={t("fe.pages.flow.steps.payment.statusScheduled")}
                          variant="scheduled"
                        />
                      )}
                    </div>
                    <div>{formatCurrency(scheduledPayment.amount || scheduledPayment?.attributes?.amount, i18n.language)}</div>
                  </div>
                )
              )}
            </section>
          </div>
        </div>
      </Form>
      <AppDialog open={openDialog}
        onOpenChange={(_open) => {
          if (status === "processing") return;
          setOpenDialog(_open);
        }}
        showClose={status !== "processing"}
        {...cfg[status]}
      />
    </>
  );
};

const CardFieldsSkeleton = () => (
  <>
    <div className="mb-5">
      <Skeleton className="h-4 w-24 mt-2" />
      <Skeleton className="h-9 w-full mt-2" />
    </div>

    <div className="grid md:grid-cols-2 gap-4 mb-5">
      <div>
        <Skeleton className="h-4 w-24 mt-2" />
        <Skeleton className="h-9 w-full mt-2" />
      </div>
      <div>
        <Skeleton className="h-4 w-24 mt-2" />
        <Skeleton className="h-9 w-full mt-2" />
      </div>
    </div>
  </>
);

